import { useMemo } from "react";

import { getAppStatusFromCache } from "./useSystemApps";

export type AppStatusKey = "healthy" | "degraded" | "stopped" | "unknown" | "update";

export type AppStatusMeta = {
  status: AppStatusKey;
  installedVersion: string | null;
  latestVersion: string | null;
  updateAvailable: boolean;
  /** Whether the upstream package is available for download. Defaults to true. */
  packageAvailable: boolean;
  updatedAt: string | null;
  message?: string;
  managePath?: string;
};

const FALLBACK_STATUS: AppStatusMeta = {
  status: "unknown",
  installedVersion: null,
  latestVersion: null,
  updateAvailable: false,
  packageAvailable: true,
  updatedAt: null,
  message: "Status unavailable",
};

export const useAppStatus = (appId: string) => {
  const data = useMemo<AppStatusMeta>(() => {
    const cached = getAppStatusFromCache(appId);
    if (!cached) return FALLBACK_STATUS;

    // Derive a user-facing message from the status data.
    // Stopped is always primary, but include update info when available.
    let message: string | undefined;
    if (!cached.active) {
      message = cached.updateAvailable && cached.latestVersion
        ? `Service stopped — update available: ${cached.latestVersion}`
        : "Service stopped";
    } else if (cached.updateAvailable && cached.latestVersion) {
      message = `Update available: ${cached.latestVersion}`;
    }

    // Map backend status to UI status key.
    // Use `active` boolean directly (ground truth from systemd) rather than derived `status`.
    // Priority: stopped (red) > update (amber, only when running) > healthy (green)
    let status: AppStatusKey;
    if (!cached.active) {
      // Service is not running - always show stopped (red)
      status = "stopped";
    } else if (cached.updateAvailable) {
      // Running with update available - show update (amber)
      status = "update";
    } else {
      // Running, no updates - show healthy (green)
      status = "healthy";
    }

    return {
      status,
      installedVersion: cached.installedVersion,
      latestVersion: cached.latestVersion,
      updateAvailable: cached.updateAvailable,
      packageAvailable: cached.packageAvailable !== false,
      updatedAt: null, // Not available from current API
      message,
    };
  }, [appId]);

  return { data, loading: false, error: undefined as string | undefined };
};
